import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import useMyFetch from '@stores/useMyFetch'
import type { MasterReport, MasterReportListItem } from '@/types'
import { captureEvent } from '@/lib/analytics'

type MasterReportInput = { name: string; active?: boolean; account_ids?: number[]; is_system?: boolean; is_qualitative?: boolean }

function sameIds(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false
  const sortedB = [...b].sort((x, y) => x - y)
  return [...a].sort((x, y) => x - y).every((v, i) => v === sortedB[i])
}

function errorMessage(data: unknown, fallback: string): string {
  const body = data as { error?: unknown; errors?: unknown } | null
  const raw = body?.errors ?? body?.error
  if (raw == null) return fallback
  if (Array.isArray(raw)) return raw.join(', ')
  if (typeof raw === 'object') return JSON.stringify(raw)
  return String(raw)
}

export const useMasterReportsStore = defineStore('masterReports', () => {
  const masterReports = ref<MasterReportListItem[]>([])
  const availableMasterReports = ref<MasterReportListItem[]>([])
  const currentMasterReport = ref<MasterReport | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Edit state for Setup page
  const editedName = ref('')
  const editedActive = ref(true)
  const editedAccountIds = ref<number[]>([])
  const editedIsSystem = ref(false)
  const editedIsQualitative = ref(false)
  const isSaving = ref(false)

  const hasChanges = computed(() => {
    if (!currentMasterReport.value) return false
    const name = editedName.value.trim()
    const nameChanged = name !== '' && name !== currentMasterReport.value.name
    const activeChanged = editedActive.value !== currentMasterReport.value.active
    const accountsChanged = !sameIds(editedAccountIds.value, currentMasterReport.value.account_ids ?? [])
    const isSystemChanged = editedIsSystem.value !== currentMasterReport.value.is_system
    const qualitativeChanged = editedIsQualitative.value !== currentMasterReport.value.is_qualitative
    return nameChanged || activeChanged || accountsChanged || isSystemChanged || qualitativeChanged
  })

  const sortedMasterReports = computed(() =>
    [...masterReports.value].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    ),
  )

  async function fetchMasterReports() {
    loading.value = true
    error.value = null

    const { response, data } = await useMyFetch('/master_reports').get().json<MasterReportListItem[]>()

    if (response.value.ok) {
      masterReports.value = data.value ?? []
    } else {
      error.value = errorMessage(data.value, 'Failed to fetch master reports')
    }

    loading.value = false
    return masterReports.value
  }

  // Templates selectable for a report on `surveyToken`'s survey: the survey
  // account's active templates + system. Access is gated by survey membership
  // server-side (the survey, not the actor, drives the scope).
  async function fetchAvailableMasterReports(surveyToken?: string) {
    loading.value = true
    error.value = null

    const url = surveyToken ? `/master_reports?survey_token=${surveyToken}` : '/master_reports'
    const { response, data } = await useMyFetch(url).get().json<MasterReportListItem[]>()

    if (response.value.ok) {
      availableMasterReports.value = data.value ?? []
    } else {
      error.value = errorMessage(data.value, 'Failed to fetch master reports')
    }

    loading.value = false
    return availableMasterReports.value
  }

  async function fetchMasterReport(token: string) {
    loading.value = true
    error.value = null

    const { response, data } = await useMyFetch(`/master_reports/${token}`).get().json<MasterReport>()

    if (response.value.ok && data.value) {
      currentMasterReport.value = data.value
      const index = masterReports.value.findIndex((t) => t.token === token)
      if (index !== -1) masterReports.value[index] = data.value
    } else if (!response.value.ok) {
      error.value = errorMessage(data.value, 'Failed to fetch master report')
    }

    loading.value = false
    return currentMasterReport.value
  }

  async function createMasterReport(params: MasterReportInput) {
    loading.value = true
    error.value = null

    const { response, data } = await useMyFetch('/master_reports').post({ master_report: params }).json<MasterReport>()

    if (response.value.ok && data.value) {
      masterReports.value.unshift(data.value)
      loading.value = false
      captureEvent('master_report_created', { master_report_token: data.value.token })
      return data.value
    } else {
      error.value = errorMessage(data.value, 'Failed to create master report')
      loading.value = false
      throw new Error(error.value)
    }
  }

  async function updateMasterReport(token: string, params: Partial<MasterReportInput>) {
    const { response, data } = await useMyFetch(`/master_reports/${token}`).patch({ master_report: params }).json<MasterReport>()

    if (response.value.ok && data.value) {
      captureEvent('master_report_updated', { master_report_token: token })

      const index = masterReports.value.findIndex((t) => t.token === token)
      if (index !== -1) masterReports.value[index] = data.value
      if (currentMasterReport.value?.token === token) {
        // Merge to preserve slide_templates and chart_styles
        currentMasterReport.value = { ...currentMasterReport.value, ...data.value }
      }
      return data.value
    } else {
      throw new Error(errorMessage(data.value, 'Failed to update master report'))
    }
  }

  async function deleteMasterReport(token: string) {
    const { response, data } = await useMyFetch(`/master_reports/${token}`).delete().json()

    if (response.value.ok || response.value.status === 204) {
      captureEvent('master_report_deleted', { master_report_token: token })

      masterReports.value = masterReports.value.filter((t) => t.token !== token)
      if (currentMasterReport.value?.token === token) currentMasterReport.value = null
      return true
    } else {
      throw new Error(errorMessage(data.value, 'Failed to delete master report'))
    }
  }

  async function duplicateMasterReport(token: string, newName: string | null = null) {
    loading.value = true
    error.value = null

    const params = newName ? { name: newName } : {}
    const { response, data } = await useMyFetch(`/master_reports/${token}/duplicate`).post(params).json<MasterReport>()

    if (response.value.ok && data.value) {
      masterReports.value.unshift(data.value)
      loading.value = false
      captureEvent('master_report_duplicated', { master_report_token: token })
      return data.value
    } else {
      error.value = errorMessage(data.value, 'Failed to duplicate master report')
      loading.value = false
      throw new Error(error.value)
    }
  }

  function setEditedName(name: string) {
    editedName.value = name
  }

  function setEditedActive(value: boolean) {
    editedActive.value = value
  }

  function setEditedAccountIds(value: number[]) {
    editedAccountIds.value = value
  }

  function setEditedIsSystem(value: boolean) {
    editedIsSystem.value = value
  }

  function setEditedIsQualitative(value: boolean) {
    editedIsQualitative.value = value
  }

  function initEditState() {
    if (currentMasterReport.value) {
      editedName.value = currentMasterReport.value.name || ''
      editedActive.value = currentMasterReport.value.active ?? true
      editedAccountIds.value = [...(currentMasterReport.value.account_ids ?? [])]
      editedIsSystem.value = currentMasterReport.value.is_system ?? false
      editedIsQualitative.value = currentMasterReport.value.is_qualitative ?? false
    }
  }

  async function saveSetup() {
    if (!hasChanges.value || !currentMasterReport.value) return

    isSaving.value = true
    try {
      const params: Partial<MasterReportInput> = {}
      if (editedName.value.trim() !== currentMasterReport.value.name) {
        params.name = editedName.value.trim()
      }
      if (editedActive.value !== currentMasterReport.value.active) {
        params.active = editedActive.value
      }
      if (!sameIds(editedAccountIds.value, currentMasterReport.value.account_ids ?? [])) {
        params.account_ids = editedAccountIds.value
      }
      if (editedIsSystem.value !== currentMasterReport.value.is_system) {
        params.is_system = editedIsSystem.value
      }
      if (editedIsQualitative.value !== currentMasterReport.value.is_qualitative) {
        params.is_qualitative = editedIsQualitative.value
      }
      await updateMasterReport(currentMasterReport.value.token, params)
    } catch (err) {
      console.error('Failed to save master report:', err)
    } finally {
      isSaving.value = false
    }
  }

  return {
    masterReports,
    availableMasterReports,
    sortedMasterReports,
    currentMasterReport,
    loading,
    error,
    editedName,
    editedActive,
    editedAccountIds,
    editedIsSystem,
    editedIsQualitative,
    hasChanges,
    isSaving,
    fetchMasterReports,
    fetchAvailableMasterReports,
    fetchMasterReport,
    createMasterReport,
    updateMasterReport,
    deleteMasterReport,
    duplicateMasterReport,
    setEditedName,
    setEditedActive,
    setEditedAccountIds,
    setEditedIsSystem,
    setEditedIsQualitative,
    initEditState,
    saveSetup,
  }
})
